import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Button, Tooltip } from "@mui/material";

import { type JobListItemDto, JobStatus } from "Infrastructure/Api/Api";
import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { isJobLocked } from "Projects/Utils/jobStatusUtils";
import {
  type BlDetailSectionItem,
  DetailSection,
} from "Shared/Sections/DetailSection";
import { DateFormat, formatDate } from "Shared/Utils/Format/date";

interface ProjectJobsProps {
  jobs: JobListItemDto[];
  projectId: string;
  onAddJob?: () => void;
  isEditable?: boolean;
  jobsLimit?: number;
  currentJobsCount?: number;
}

export const ProjectJobs = ({
  jobs,
  projectId,
  onAddJob,
  isEditable = true,
  jobsLimit,
  currentJobsCount,
}: ProjectJobsProps): React.ReactNode => {
  const { t } = useResource();
  const navigate = useAppNavigate();

  const isLimitReached =
    jobsLimit !== undefined &&
    currentJobsCount !== undefined &&
    jobsLimit > 0 &&
    currentJobsCount >= jobsLimit;

  const action = onAddJob && isEditable && (
    <Tooltip
      title={
        isLimitReached
          ? t(Resources.Projects.ProjectDetail.JobsLimitExceeded)
          : ""
      }
    >
      <span>
        <Button
          variant="text"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onAddJob}
          disabled={isLimitReached}
          sx={{ fontSize: "16px", textTransform: "none" }}
        >
          {t(Resources.Projects.ProjectDetail.AddJobToProject)}
        </Button>
      </span>
    </Tooltip>
  );

  const items: BlDetailSectionItem[] = jobs.map((job) => ({
    id: job.jobID,
    startAdornment: getJobStatusCircle(job.status, t),
    label: { text: job.name },
    endAdornment: (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: Spacing.S,
        }}
      >
        {isJobLocked(job.status) && (
          <Tooltip title={t(Resources.Projects.ProjectDetail.JobLocked)}>
            <LockOutlinedIcon
              sx={{ fontSize: 16, color: "text.secondary" }}
              aria-label={t(Resources.Projects.ProjectDetail.JobLocked)}
            />
          </Tooltip>
        )}
        <DetailSection.MetaText>
          {formatDate(job.dateCreated, DateFormat.DateTime)}
        </DetailSection.MetaText>
      </Box>
    ),
    onClick: () =>
      navigate(AppRouting.ProjectJobDetail.path(projectId, job.jobID)),
  }));

  return (
    <DetailSection
      title={t(Resources.Projects.ProjectDetail.Jobs)}
      action={action || undefined}
      items={items}
    />
  );
};

const getJobStatusCircle = (
  status: JobStatus,
  t: ReturnType<typeof useResource>["t"],
): React.ReactNode => {
  if (status === JobStatus.Done) {
    return (
      <DetailSection.StatusCircle
        color="success.main"
        label={t(Resources.Common.StatusDone)}
      >
        <CheckCircleIcon sx={{ color: "white", fontSize: 18 }} />
      </DetailSection.StatusCircle>
    );
  }

  if (status === JobStatus.WaitingForApproval) {
    return (
      <DetailSection.StatusCircle
        color="warning.main"
        label={t(Resources.Common.StatusWaitingForApproval)}
      >
        <HourglassEmptyRoundedIcon sx={{ color: "white", fontSize: 18 }} />
      </DetailSection.StatusCircle>
    );
  }

  if (status === JobStatus.RejectedByActor) {
    return (
      <DetailSection.StatusCircle
        color="error.main"
        label={t(Resources.Common.StatusRejectedByActor)}
      >
        <CancelIcon sx={{ color: "white", fontSize: 18 }} />
      </DetailSection.StatusCircle>
    );
  }

  return (
    <DetailSection.StatusCircle
      color="info.main"
      label={t(Resources.Common.StatusInProgress)}
    >
      <EditIcon sx={{ color: "white", fontSize: 18 }} />
    </DetailSection.StatusCircle>
  );
};
